import { useEffect, useRef, useState } from 'react';
import type { AiAttachableDocument } from '@accounting/shared';
import { AttachDocumentPicker } from './AttachDocumentPicker';

interface Props {
  streaming: boolean;
  canRegenerate: boolean;
  onSend: (text: string, attachedDocumentIds: string[]) => void;
  onStop: () => void;
  onRegenerate: () => void;
  onAttachmentsChange: (docs: AiAttachableDocument[]) => void;
}

/**
 * The input row: type, attach, send / stop, regenerate.
 *
 * A textarea rather than an input because real questions about books run to
 * several lines. Enter sends; Shift+Enter breaks the line.
 */
export function ChatComposer({
  streaming,
  canRegenerate,
  onSend,
  onStop,
  onRegenerate,
  onAttachmentsChange,
}: Props) {
  const [text, setText] = useState('');
  const [attached, setAttached] = useState<AiAttachableDocument[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const areaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    onAttachmentsChange(attached);
  }, [attached, onAttachmentsChange]);

  // Grow with the content, up to a few lines.
  useEffect(() => {
    const el = areaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [text]);

  function submit() {
    const value = text.trim();
    if (!value || streaming) return;
    onSend(
      value,
      attached.map((d) => d.id),
    );
    setText('');
    setAttached([]);
  }

  function toggleDoc(doc: AiAttachableDocument) {
    setAttached((prev) =>
      prev.some((d) => d.id === doc.id)
        ? prev.filter((d) => d.id !== doc.id)
        : [...prev, doc].slice(0, 5),
    );
  }

  return (
    <div style={{ marginTop: 12 }}>
      {attached.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
          {attached.map((d) => (
            <span
              key={d.id}
              style={{
                fontSize: 12,
                padding: '2px 8px',
                borderRadius: 999,
                background: 'var(--bg-surface-2)',
                display: 'inline-flex',
                gap: 6,
                alignItems: 'center',
              }}
            >
              📎 {d.fileName}
              <button
                type="button"
                aria-label={`Remove ${d.fileName}`}
                onClick={() => toggleDoc(d)}
                style={{ all: 'unset', cursor: 'pointer', opacity: 0.7 }}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}
      >
        <button
          type="button"
          className="btn-secondary"
          title="Attach a document from the vault"
          aria-label="Attach a document"
          onClick={() => setPickerOpen(true)}
        >
          📎
        </button>

        <textarea
          ref={areaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder="Ask about your companies, balances, P&L… (Shift+Enter for a new line)"
          rows={1}
          autoFocus
          style={{ flex: 1, resize: 'none', lineHeight: 1.5, fontFamily: 'inherit' }}
        />

        {canRegenerate && !streaming && (
          <button
            type="button"
            className="btn-secondary"
            title="Regenerate the last answer"
            onClick={onRegenerate}
          >
            ↻
          </button>
        )}

        {streaming ? (
          <button type="button" className="btn-secondary" onClick={onStop}>
            Stop
          </button>
        ) : (
          <button type="submit" className="btn-primary" disabled={!text.trim()}>
            Send
          </button>
        )}
      </form>

      <AttachDocumentPicker
        open={pickerOpen}
        selectedIds={attached.map((d) => d.id)}
        onToggle={toggleDoc}
        onClose={() => setPickerOpen(false)}
      />
    </div>
  );
}
